import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Calendar, 
  Heart, 
  User, 
  Target, 
  Compass,
  ExternalLink,
  Mail,
  Download,
  Wifi,
  WifiOff,
  Share2,
  Smartphone,
  GraduationCap,
  Church,
  Users,
  ArrowRight
} from 'lucide-react';
import ContentAccordion from '../components/common/ContentAccordion';
import ContentCard from '../components/common/ContentCard';
import { retreatContent } from '../data/retreatContent';

const AboutPage = () => {
  const navigate = useNavigate();
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : 'https://lenten-journey-3.preview.emergentagent.com';

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <img 
          src={retreatContent.logo}
          alt="DMU"
          className="w-20 h-20 mx-auto rounded-full shadow-lg mb-4"
        />
        <h1 className="font-heading text-xl text-[var(--primary)] font-bold mb-1">
          30-Day Ignatian Lenten Journey to Flourishing
        </h1>
        <p className="text-gray-500 text-sm">Divine Mercy University</p>
      </div>

      {/* Share QR Code */}
      <div className="bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-4">
          <img 
            src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(shareUrl)}&bgcolor=FFFFFF&color=5D1F2F`}
            alt="QR Code to share"
            className="w-24 h-24 rounded-lg bg-white p-1"
          />
          <div className="flex-1">
            <h3 className="font-semibold mb-1 flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Share This Retreat
            </h3>
            <p className="text-white/80 text-xs mb-2">
              Scan the QR code or share the link with others
            </p>
            <p className="text-[var(--secondary)] text-xs break-all">
              {shareUrl.replace('https://', '')}
            </p>
          </div>
        </div>
      </div>

      {/* What is this Retreat? */}
      <ContentAccordion 
        title="What is this Retreat?" 
        summary="How the Spiritual Exercises, 19th Annotation & RIMP work together"
        defaultOpen={true}
      >
        <div className="space-y-4 text-sm text-gray-600">
          <p>
            This retreat integrates <strong>three powerful spiritual traditions</strong> into one transformative 30-day journey:
          </p>
          
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <h4 className="font-semibold text-[var(--primary)] mb-1">1. The Spiritual Exercises</h4>
            <p className="text-xs">
              Written by St. Ignatius of Loyola in the 1500s, the <strong>Spiritual Exercises</strong> are a profound 30-day retreat designed to help you encounter God and discern His will. They have guided millions of souls for nearly 500 years.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <h4 className="font-semibold text-[var(--primary)] mb-1">2. The 19th Annotation</h4>
            <p className="text-xs">
              St. Ignatius recognized that not everyone can leave their daily life for 30 days. <strong>Annotation 19</strong> allows you to do the Exercises while maintaining your regular responsibilities — a "retreat in daily life."
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <h4 className="font-semibold text-[var(--primary)] mb-1">3. The RIMP Way</h4>
            <p className="text-xs">
              Developed by <strong>Tony MacDonnell</strong> (Campus Ministry, DMU), RIMP maps the four "weeks" of the Exercises to human flourishing: <strong>R</strong>elationship, <strong>I</strong>dentity, <strong>M</strong>ission, <strong>P</strong>urpose.
            </p>
          </div>
          
          <div className="bg-[var(--primary)] text-white rounded-lg p-3">
            <p className="text-xs">
              Together, these create a <strong>30-Day Ignatian Lenten Journey to Flourishing</strong> — combining ancient spiritual depth, practical flexibility, and modern psychological insight.
            </p>
          </div>
        </div>
      </ContentAccordion>

      {/* Hybrid Retreat Explanation */}
      <ContentAccordion 
        title="A Hybrid Retreat Experience" 
        summary="Combining traditional wisdom with modern technology"
      >
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            This retreat blends the timeless wisdom of the Spiritual Exercises with modern tools:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <Smartphone className="w-4 h-4 text-[var(--primary)] mt-0.5 flex-shrink-0" />
              <span><strong>Digital journal</strong> to track your spiritual movements</span>
            </li>
            <li className="flex items-start gap-2">
              <Download className="w-4 h-4 text-[var(--primary)] mt-0.5 flex-shrink-0" />
              <span><strong>Downloadable PDF</strong> for offline reflection</span>
            </li>
            <li className="flex items-start gap-2">
              <WifiOff className="w-4 h-4 text-[var(--primary)] mt-0.5 flex-shrink-0" />
              <span><strong>Offline access</strong> - install as an app on your phone</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-4 h-4 text-[var(--primary)] mt-0.5 flex-shrink-0" />
              <span><strong>Cloud sync</strong> to save your progress across devices</span>
            </li>
          </ul>
        </div>
      </ContentAccordion>

      {/* The Four Stages */}
      <div>
        <h3 className="font-heading font-semibold text-[var(--primary)] mb-3">The Four Stages (RIMP)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Ignatius called them "Weeks" but they're actually <strong>thematic stages</strong>, not literal 7-day periods.
        </p>
        <div className="space-y-3">
          <div className="flex items-center gap-3 bg-rose-50 rounded-xl p-3 border border-rose-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-rose-700 text-sm">Week 1: Relationship</h4>
              <p className="text-xs text-rose-600">God's love & mercy • Days 1-7</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-amber-50 rounded-xl p-3 border border-amber-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-amber-700 text-sm">Week 2: Identity</h4>
              <p className="text-xs text-amber-600">Walking with Jesus • Days 8-14</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-teal-50 rounded-xl p-3 border border-teal-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-teal-700 text-sm">Week 3: Mission</h4>
              <p className="text-xs text-teal-600">The Passion of Christ • Days 15-21</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-violet-50 rounded-xl p-3 border border-violet-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-violet-700 text-sm">Week 4: Purpose</h4>
              <p className="text-xs text-violet-600">The Resurrection • Days 22-30</p>
            </div>
          </div>
        </div>
      </div>

      {/* Spiritual Direction */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
        <h3 className="font-heading font-semibold text-[var(--primary)] mb-3 flex items-center gap-2">
          <GraduationCap className="w-5 h-5" />
          Need a Spiritual Director?
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          While this retreat can be done independently, spiritual direction can deepen your experience.
        </p>
        <div className="space-y-2">
          <a 
            href={`mailto:${retreatContent.resourceLinks.dmuResources[0].email}`}
            className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200 hover:border-[var(--primary)] transition-colors"
          >
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[var(--primary)]" />
              <div>
                <p className="font-medium text-gray-800 text-sm">DMU Campus Ministry</p>
                <p className="text-xs text-gray-500">Email for spiritual direction</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </a>
          <a 
            href={retreatContent.resourceLinks.dmuResources[1].url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200 hover:border-[var(--primary)] transition-colors"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[var(--primary)]" />
              <div>
                <p className="font-medium text-gray-800 text-sm">Alumni Spiritual Directors</p>
                <p className="text-xs text-gray-500">Connect with DMU graduates</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </a>
        </div>
      </div>

      {/* Recommended Apps */}
      <div>
        <h3 className="font-heading font-semibold text-[var(--primary)] mb-3 flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Recommended Prayer Apps
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {retreatContent.resourceLinks.prayerApps.map((app) => (
            <a
              key={app.name}
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-3 border border-gray-100 hover:shadow-md transition-shadow text-center"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] mx-auto mb-2 flex items-center justify-center">
                <span className="text-white font-bold text-lg">{app.name[0]}</span>
              </div>
              <p className="font-medium text-gray-800 text-xs">{app.name}</p>
            </a>
          ))}
        </div>
      </div>

      {/* Regnum Christi Retreats */}
      <a 
        href="https://regnumchristi.org/en/locations/"
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-gradient-to-r from-[#1B93A4] to-[#157a8a] rounded-xl p-4 text-white hover:shadow-lg transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Church className="w-8 h-8" />
            <div>
              <h4 className="font-semibold">Retreats and Events in RC Near You</h4>
              <p className="text-white/80 text-xs">Find RC communities, retreats & spiritual programs</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5" />
        </div>
      </a>

      {/* Features */}
      <ContentAccordion title="App Features" summary="What this app offers">
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <WifiOff className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-800 text-sm">Offline Access</h4>
              <p className="text-xs text-gray-500">Install as an app and use without internet</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Download className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-800 text-sm">PDF Download</h4>
              <p className="text-xs text-gray-500">Print the full retreat for offline use</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Wifi className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-800 text-sm">Cloud Sync</h4>
              <p className="text-xs text-gray-500">Save progress across all your devices</p>
            </div>
          </div>
        </div>
      </ContentAccordion>

      {/* Learn More */}
      <div className="grid grid-cols-2 gap-3">
        <ContentCard
          title="The RIMP Way"
          subtitle="Deep dive"
          onClick={() => navigate('/reference/rimp')}
        />
        <ContentCard
          title="Discernment"
          subtitle="Ignatian rules"
          onClick={() => navigate('/reference/discernment')}
        />
      </div>

      {/* Footer */}
      <div className="text-center pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          © {new Date().getFullYear()} Divine Mercy University
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Legion of Christ & Regnum Christi Movement
        </p>
      </div>
    </div>
  );
};

export default AboutPage;
